import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Input, Button } from '@artstorefronts/ui';
import { toast } from 'sonner';
import { PencilIcon, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ChatRenameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentTitle: string;
  onConfirm: (newTitle: string) => Promise<void>;
  isLoading?: boolean;
}

export function ChatRenameDialog({
  open,
  onOpenChange,
  currentTitle,
  onConfirm,
  isLoading = false,
}: ChatRenameDialogProps) {
  const [newTitle, setNewTitle] = useState(currentTitle);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation('chat');
  const { t: tc } = useTranslation('common');

  // Reset form when dialog opens/closes or currentTitle changes
  useEffect(() => {
    if (open) {
      setNewTitle(currentTitle);
    }
  }, [open, currentTitle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newTitle.trim()) {
      toast.error(t('rename-dialog.title-empty'));
      return;
    }

    if (newTitle.trim() === currentTitle) {
      onOpenChange(false);
      return;
    }

    setIsSubmitting(true);
    await onConfirm(newTitle.trim());
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    setNewTitle(currentTitle);
    onOpenChange(false);
  };

  const isSubmitDisabled = !newTitle.trim() || newTitle.trim() === currentTitle || isSubmitting || isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <PencilIcon className="w-6 h-6 mr-2" />
            {t('rename-dialog.title')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid pb-4">
            <Input
              id="chat-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder={t('rename-dialog.placeholder')}
              disabled={isSubmitting || isLoading}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting || isLoading}>
              {tc('cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitDisabled}>
              {(isSubmitting || isLoading) && <Loader2 className="size-4 animate-spin" />}
              {t('rename-dialog.rename')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
